// ─── How it works — shared UI blocks ───────────────────────
// Small reusable presentational pieces so each section stays lean
// and the whole guide keeps a consistent visual language.

interface SectionTitleProps {
  id?: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  kicker: string;
  title: string;
  subtitle?: string;
}

export function SectionTitle({ id, icon, iconBg, iconColor, kicker, title, subtitle }: SectionTitleProps) {
  return (
    <div id={id} className="scroll-mt-20 mb-6">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
          <i className={`${icon} ${iconColor} text-lg w-5 h-5 flex items-center justify-center`}></i>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">{kicker}</p>
          <h2 className="text-lg font-heading font-bold text-foreground-950">{title}</h2>
        </div>
      </div>
      {subtitle && <p className="text-foreground-600 text-sm mt-3 leading-relaxed max-w-3xl">{subtitle}</p>}
    </div>
  );
}

interface CardProps {
  children: React.ReactNode;
  className?: string;
}

export function Card({ children, className = '' }: CardProps) {
  return (
    <div className={`bg-background-100 rounded-xl border border-background-200/70 p-5 ${className}`}>
      {children}
    </div>
  );
}

interface PillProps {
  children: React.ReactNode;
  tone?: 'primary' | 'accent' | 'secondary' | 'neutral';
}

const PILL_TONES: Record<string, string> = {
  primary: 'bg-primary-500/10 text-primary-400 border-primary-500/20',
  accent: 'bg-accent-500/10 text-accent-400 border-accent-500/20',
  secondary: 'bg-secondary-500/10 text-secondary-400 border-secondary-500/20',
  neutral: 'bg-background-200/70 text-foreground-600 border-background-300/50',
};

export function Pill({ children, tone = 'neutral' }: PillProps) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${PILL_TONES[tone]}`}>
      {children}
    </span>
  );
}

interface ListItemProps {
  icon: string;
  iconColor: string;
  title: string;
  children: React.ReactNode;
}

export function ListItem({ icon, iconColor, title, children }: ListItemProps) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-6 h-6 rounded-md bg-background-200/60 flex items-center justify-center shrink-0 mt-0.5">
        <i className={`${icon} ${iconColor} text-xs w-4 h-4 flex items-center justify-center`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-900 text-sm font-medium">{title}</p>
        <p className="text-foreground-600 text-xs mt-0.5 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}